import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BinanceWallet,
  MyDepositAddressResponse,
} from '../interfaces/binance-wallet.interface';

/**
 * Endpoints del cliente final para su propia wallet Binance Link.
 * El `usuarioId` se deriva del JWT — no se envía en URL ni body.
 *
 * Distinto de `BinanceWalletAdminService` (que es para operadores y opera
 * sobre `walletId` arbitrarios).
 */
@Injectable({
  providedIn: 'root',
})
export class BinanceWalletClientService {
  private baseUrl = `${environment.apiNest}binance-link/wallets`;

  constructor(private http: HttpClient) {}

  /**
   * GET /binance-link/wallets/me
   *
   * Devuelve la wallet activa del usuario autenticado, o `null` si nunca
   * creó una (404 del backend).
   *
   * Contrato actualizado (deploy 2026-06-11):
   *  - 200 → el objeto wallet directo (sin wrapper `{ wallet: ... }`).
   *  - 404 → el usuario NO tiene wallet — este método lo mapea a `null`.
   *  - 401 → token inválido — se propaga para que el interceptor global
   *    de auth se haga cargo (no es "sin wallet", es "sesión rota").
   *
   * Otros errores (5xx) se propagan también.
   */
  getMyWallet(): Promise<BinanceWallet | null> {
    return firstValueFrom(
      this.http.get<BinanceWallet>(`${this.baseUrl}/me`).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            // "Wallet no encontrada" = caso esperado, NO error real.
            return of(null);
          }
          return throwError(() => err);
        }),
      ),
    );
  }

  /**
   * GET /binance-link/wallets/me/deposit-address
   *
   * Llamada live a Binance que devuelve la dirección de depósito TRC20
   * propia del usuario autenticado. La address TRC20 es estable así que
   * el caller debe cachearla en memoria de la vista.
   *
   * Mapeo de errores:
   *  - 200 → `MyDepositAddressResponse`.
   *  - 404 → `null` (el usuario nunca creó wallet). El componente debe
   *    caer al admin wallet por default.
   *  - 409 → `null` (KYC todavía pendiente, Binance no aprobó). Mismo
   *    fallback al admin wallet — la UI no expone el detalle de Binance
   *    Link al cliente final.
   *  - 401 → propaga al interceptor global (token roto, NO confundir
   *    con "sin wallet"/"KYC pendiente").
   *  - 5xx / 502 → propaga al caller.
   */
  getMyDepositAddress(): Promise<MyDepositAddressResponse | null> {
    return firstValueFrom(
      this.http
        .get<MyDepositAddressResponse>(`${this.baseUrl}/me/deposit-address`)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === 404 || err.status === 409) {
              // 404 = sin wallet; 409 = KYC pendiente. En ambos casos
              // la UI muestra el admin wallet por default — el cliente
              // final no necesita ver el detalle de Binance Link.
              return of(null);
            }
            return throwError(() => err);
          }),
        ),
    );
  }
}
